"use client";

import { CalendarDays } from "lucide-react";
import { useFormatter, useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { cohortStatus } from "../lib/cohort-line";

type Props = {
  /** The cohort start from the record: the chosen plan's `cohortStartAt`, or the course's `nextCohortStartAt`. */
  iso: string | null | undefined;
  /** `card`: the boxed line in a pricing card; `bar`: plain text on the dark sticky bar; `chip`: a catalogue card's pill. */
  tone?: "card" | "bar" | "chip";
  className?: string;
};

/**
 * `SITE-CRS01`: "Live classes begin 12 October, in 7 days" or "Live classes
 * started on 2 October", from the record's date only; nothing when the date is
 * missing or more than a week old (`lib/cohort-line.ts`). The day count can move
 * between the prerendered page and the reader's clock, so its text is allowed to
 * differ at hydration.
 */
export function CourseCohortLine({ iso, tone = "card", className }: Props) {
  const t = useTranslations("courseDetail");
  const format = useFormatter();
  const status = cohortStatus(iso);
  if (!status || !iso) return null;
  const date = format.dateTime(new Date(iso), {
    day: "numeric",
    month: "long",
    timeZone: "Asia/Dhaka",
  });
  const text =
    status.kind === "started"
      ? t("liveClassesStarted", { date })
      : `${t("publicLiveClassesBegin", { date })}, ${t("cohortInDays", { count: status.days })}`;

  if (tone === "chip") {
    return (
      <span
        className={cn(
          "inline-flex max-w-full items-center gap-1.5 self-start rounded-full bg-primary/10 px-2.5 py-1 text-[12px] font-semibold text-primary-ink",
          className,
        )}
      >
        <CalendarDays className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate" suppressHydrationWarning>{text}</span>
      </span>
    );
  }
  if (tone === "bar") {
    return (
      <span className={cn("truncate text-[13px] font-semibold text-accent-peach", className)} suppressHydrationWarning>
        {text}
      </span>
    );
  }
  return (
    <p
      className={cn(
        "flex items-center gap-2 rounded-[10px] border border-primary/20 bg-primary/[0.05] px-3 py-2.5 text-[13px] font-semibold leading-snug text-neutral-900",
        className,
      )}
    >
      <CalendarDays className="h-4 w-4 shrink-0 text-primary" aria-hidden />
      <span suppressHydrationWarning>{text}</span>
    </p>
  );
}
